import { ChevronDown } from "lucide-react";
import { SectionHeader } from "../SectionHeader";
import ThemeChip from "../ThemeChip";
import { fmtCr } from "../../utils/format";

export function MeasTracking({ measTracking, pendingExpanded, pendingLoading, togglePendingPO }) {
  if (!measTracking.length) return null;
  return (
    <SectionHeader title="MEAS Data Submission Tracking" subtitle="Programme Officers with pending KPI submissions — sorted by volume of missing grants">
      <div className="space-y-1" data-testid="meas-tracking-section">
        <div className="flex items-center gap-5 mb-4 text-[10px] text-slate-500 uppercase font-semibold tracking-wide">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />High ≥75% pending</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />Medium 40–74%</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-400 inline-block" />Low &lt;40%</span>
          <span className="ml-auto text-slate-600 normal-case font-normal">{measTracking.length} POs with at least 1 pending grant</span>
        </div>
        {measTracking.map((r, i) => {
          const urgencyColor = r.urgency === "high" ? "bg-red-500" : r.urgency === "medium" ? "bg-amber-400" : "bg-green-400";
          const barColor = r.urgency === "high" ? "#EF4444" : r.urgency === "medium" ? "#F59E0B" : "#22C55E";
          const textColor = r.urgency === "high" ? "text-red-600" : r.urgency === "medium" ? "text-amber-600" : "text-green-600";
          const isExpanded = pendingExpanded[r.po] !== undefined;
          const isLoading = pendingLoading[r.po];
          const pendingGrants = pendingExpanded[r.po] || [];
          return (
            <div key={i} data-testid={`tracking-row-${i}`}>
              <div
                onClick={() => togglePendingPO(r.po)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${isExpanded ? "bg-red-50/30 rounded-b-none" : ""}`}>
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${urgencyColor}`} />
                <span className="font-medium text-slate-800 text-sm w-44 flex-shrink-0 truncate">{r.po}</span>
                <div className="flex-1 relative h-4 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${r.pending_pct}%`, backgroundColor: barColor }} />
                  <div className="absolute inset-0 flex items-center px-2">
                    <span className="text-[10px] font-bold" style={{ color: r.pending_pct > 40 ? "#fff" : "#94A3B8" }}>
                      {r.pending} of {r.total} grants pending
                    </span>
                  </div>
                </div>
                <span className={`text-sm font-bold w-12 text-right flex-shrink-0 ${textColor}`}>{r.pending_pct}%</span>
                <ChevronDown size={14} className={`text-slate-600 flex-shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""} ${isLoading ? "animate-spin" : ""}`} />
              </div>
              {isExpanded && pendingGrants.length > 0 && (
                <div className="border border-slate-100 border-t-0 rounded-b-lg overflow-hidden mb-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="text-left text-[10px] text-slate-500 uppercase font-semibold py-1.5 px-3">Grant ID</th>
                        <th className="text-left text-[10px] text-slate-500 uppercase font-semibold py-1.5">Grantee</th>
                        <th className="text-left text-[10px] text-slate-500 uppercase font-semibold py-1.5">Theme</th>
                        <th className="text-left text-[10px] text-slate-500 uppercase font-semibold py-1.5">FY</th>
                        <th className="text-left text-[10px] text-slate-500 uppercase font-semibold py-1.5">Status</th>
                        <th className="text-right text-[10px] text-slate-500 uppercase font-semibold py-1.5 pr-3">Sanctioned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingGrants.map((g, gi) => (
                        <tr key={gi} className="border-b border-slate-50 hover:bg-red-50/20">
                          <td className="py-1.5 px-3 font-mono text-slate-600">{g.grant_id}</td>
                          <td className="py-1.5 text-slate-700 font-medium max-w-[200px] truncate" title={g.grantee}>{g.grantee}</td>
                          <td className="py-1.5"><ThemeChip theme={g.theme} /></td>
                          <td className="py-1.5 text-slate-500 whitespace-nowrap">{g.fy}</td>
                          <td className="py-1.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${g.status === "Ongoing" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                              {g.status}
                            </span>
                          </td>
                          <td className="py-1.5 pr-3 text-right text-slate-600 font-medium">{fmtCr(g.allocation_cr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-3 py-1.5 bg-slate-50 text-[10px] text-slate-600 flex items-center justify-between">
                    <span>{pendingGrants.length} grants with no MEAS submissions</span>
                    <span>Total at risk: {fmtCr(pendingGrants.reduce((s, g) => s + g.allocation_cr, 0))}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </SectionHeader>
  );
}
